"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import clsx from "clsx";
import { useTranslations } from "next-intl";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { PORTAL_LINKS } from "@/lib/portalLinks";
import { isRegionalPath, stripLocale } from "@/i18n/config";

/**
 * Barra superior: portales a la izquierda e idioma a la derecha. Se retrae al
 * hacer scroll para que no cueste altura permanente, y no existe por debajo de
 * lg: en teléfono y tablet su contenido vive dentro del drawer.
 */
export default function TopBar({ isScrolled }: { isScrolled: boolean }) {
  const t = useTranslations("topbar");
  const pathname = usePathname();
  // El middleware de next-intl antepone el locale; para comparar rutas sobra.
  const path = stripLocale(pathname);

  // Los portales regionales viven en rutas propias (/mppa, /mpda…), no bajo
  // /aeropuertos, así que ese enlace no puede resolverse sólo por prefijo.
  const isActive = (key: string, href: string) => {
    if (key === "regional") return isRegionalPath(path) || path.startsWith(href);
    return href === "/" ? path === "/" : path.startsWith(href);
  };

  return (
    <div
      aria-hidden={isScrolled}
      className={clsx(
        "hidden lg:block bg-[var(--fondoGeneral2)] overflow-hidden transition-all duration-300",
        isScrolled
          ? "max-h-0 opacity-0 pointer-events-none"
          : "max-h-12 opacity-100",
      )}
    >
      <div className="w-[92%] max-w-7xl mx-auto flex items-center justify-between py-2">
        <nav aria-label={t("airport")}>
          <ul className="flex items-center gap-6">
            {PORTAL_LINKS.map((portal) => {
              const active = isActive(portal.key, portal.href);
              return (
                <li key={portal.key}>
                  <Link
                    href={portal.href}
                    tabIndex={isScrolled ? -1 : undefined}
                    aria-current={active ? "page" : undefined}
                    className={clsx(
                      "text-xs font-medium tracking-wide transition-colors duration-200",
                      active
                        ? "text-[var(--acento-nav)]"
                        : "text-[var(--blanco)]/60 hover:text-[var(--blanco)]",
                    )}
                  >
                    {t(portal.key)}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <LanguageSwitcher variant="inline" />
      </div>
    </div>
  );
}
